/**
 * The band connect walk in a small sign-in popup, under `symphonyConnectWalkPopupWeb`, instead of a new tab.
 *
 * Not an iframe: Google, Meta and Slack answer `X-Frame-Options: DENY` / `SAMEORIGIN` on their consent
 * screens, so a framed walk would stop at its first provider. The walk's finish page closes its own window
 * (`window.close()`, which a browser allows for any window SCRIPT opened), so a popup ends exactly as the
 * tab did. While the popup is open the app is dimmed (`ConnectWalkScrim`), which reads this store.
 *
 * Import-free, so the geometry and the store are testable without the app's graph.
 */

/** The single-app connect's popup size (`use-connect-connector`), so both sign-in windows match. */
export const WALK_POPUP_SIZE = { width: 520, height: 720 } as const;
// Unguessable per page load, so no other page can pre-open a window under this name for us to reuse.
const WALK_WINDOW_NAME = `symphony-connect-walk-${Math.random().toString(36).slice(2)}`;

export interface WindowBox {
  screenX: number;
  screenY: number;
  outerWidth: number;
  outerHeight: number;
}

export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The popup's middle sits at 40% of the target's height: a little above centre, by request. */
const VERTICAL_BIAS = 0.4;
const CHAT_PANEL_SELECTOR = '[data-chat-panel]';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, Math.max(min, max)));

/**
 * Window features for a popup centred on `target` (the chat, in screen pixels) and slightly above its
 * middle, falling back to the whole Symphony window. Clamped inside that window, so a popup wider than
 * the docked chat slides inward rather than past the window's edge.
 */
export function centeredPopupFeatures(
  box: WindowBox,
  target: ScreenRect = { left: box.screenX, top: box.screenY, width: box.outerWidth, height: box.outerHeight },
  size: { width: number; height: number } = WALK_POPUP_SIZE,
): string {
  const left = clamp(target.left + (target.width - size.width) / 2, box.screenX, box.screenX + box.outerWidth - size.width);
  const top = clamp(target.top + (target.height - size.height) * VERTICAL_BIAS, box.screenY, box.screenY + box.outerHeight - size.height);
  return `popup=yes,width=${size.width},height=${size.height},left=${Math.round(left)},top=${Math.round(top)}`;
}

type WalkWindow = Pick<Window, 'open'> & WindowBox & Partial<Pick<Window, 'document' | 'innerWidth' | 'innerHeight' | 'matchMedia'>>;

/** The app's narrow breakpoint (`useNarrow`, 720). */
const NARROW_QUERY = '(max-width: 719px)';

/**
 * A phone browser ignores popup features and opens a TAB, and it cannot bring that tab back from the dim
 * (`focus()` does not switch tabs) nor offer Escape. So a narrow screen keeps the tab it always had and
 * gets no dim: the closest web match to the apps' in-app browser.
 */
function walkCanPopUp(win: WalkWindow): boolean {
  return !win.matchMedia?.(NARROW_QUERY).matches;
}

/**
 * The chat panel's box in SCREEN pixels. The page's offset inside the browser window is the browser's own
 * chrome: the toolbars above it (`outerHeight - innerHeight`) and a side border, split evenly.
 */
function chatScreenRect(win: WalkWindow): ScreenRect | undefined {
  const rect = win.document?.querySelector(CHAT_PANEL_SELECTOR)?.getBoundingClientRect();
  if (!rect || rect.width === 0 || win.innerWidth === undefined || win.innerHeight === undefined) return undefined;
  const chromeX = Math.max(0, (win.outerWidth - win.innerWidth) / 2);
  const chromeY = Math.max(0, win.outerHeight - win.innerHeight);
  return { left: win.screenX + chromeX + rect.left, top: win.screenY + chromeY + rect.top, width: rect.width, height: rect.height };
}

let openWalk: Window | null = null;
const listeners = new Set<() => void>();

function setOpenWalk(next: Window | null): void {
  if (openWalk === next) return;
  openWalk = next;
  for (const listener of [...listeners]) listener();
}

export function subscribeConnectWalk(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function currentConnectWalk(): Window | null {
  return openWalk;
}

/** Called when the popup is gone. Only the walk being tracked can clear it, so a stale timer cannot. */
export function clearConnectWalk(walk: Window): void {
  if (openWalk === walk) setOpenWalk(null);
}

/**
 * Opens the window blank, cuts its `opener`, and only then navigates it, so the provider pages it passes
 * through never get a handle on the Symphony window. Not `noopener`, which returns no handle: the scrim
 * needs one to watch `closed`. The finish page can still close the window, because SCRIPT opened it.
 */
function openDetached(win: Pick<Window, 'open'>, url: string, target: string, features?: string): Window | null {
  const opened = win.open('', target, features);
  if (!opened) return null;
  try {
    opened.opener = null;
  } catch {
    // A reused popup that already left our origin; it is ours, opened by an earlier walk.
  }
  opened.location.href = url;
  return opened;
}

/**
 * Opens the walk. As a popup when the experiment is on; a blocked popup, or the experiment off, opens the
 * tab it always did. Re-opening reuses the one named window, so a second click restarts the walk rather
 * than stacking windows.
 */
export function openConnectWalk(url: string, asPopup: boolean, win: WalkWindow = window): void {
  if (asPopup && walkCanPopUp(win)) {
    const popup = openDetached(win, url, WALK_WINDOW_NAME, centeredPopupFeatures(win, chatScreenRect(win)));
    if (popup) {
      setOpenWalk(popup);
      return;
    }
  }
  openDetached(win, url, '_blank');
}
